// Parses a bank account statement CSV and groups outgoing transactions by
// a normalized payee. Consumed by the "Kontoudtræk (bank)" tab in the
// import modal; the user then maps each group to an existing fast item
// so its `actuals[month]` gets populated.

const BOM = '﻿';

export interface BankTransaction {
  month: number; // 0..11
  text: string;
  amount: number; // positive number, in whole DKK
}

export interface BankPayeeGroup {
  id: string;
  label: string; // display label (from the first transaction text)
  count: number;
  total: number;
  months: Record<number, number>; // month index -> total spent that month
}

export interface BankParseResult {
  groups: BankPayeeGroup[];
  txnCount: number;
  /** Transactions dropped because they belong to a different year than targetYear. */
  skippedYearCount: number;
}

export interface BankParseOptions {
  /**
   * Day of month (1..28) that starts the accounting period. A transaction
   * dated day D belongs to that calendar month if D >= startDay, otherwise
   * to the previous calendar month. Default 1 (calendar month = accounting
   * month).
   */
  budgetStartDay?: number;
  /**
   * If set, transactions whose budget period year doesn't match this year
   * are filtered out and counted in `skippedYearCount`. Prevents e.g.
   * December-2025 transactions leaking into a 2026 import.
   */
  targetYear?: number;
}

/**
 * Map a real date to the budget month index (0..11) using budgetStartDay.
 * Year-agnostic — see budgetPeriodFor when the calendar year matters.
 */
export function budgetMonthFor(date: Date, budgetStartDay: number): number {
  return budgetPeriodFor(date, budgetStartDay).month;
}

/**
 * Map a real date to the budget period {year, month} using budgetStartDay.
 * When the shift wraps January → December, the year also decrements by 1.
 */
export function budgetPeriodFor(
  date: Date,
  budgetStartDay: number,
): { year: number; month: number } {
  const day = date.getDate();
  let month = date.getMonth();
  let year = date.getFullYear();
  if (day < budgetStartDay) {
    month -= 1;
    if (month < 0) {
      month = 11;
      year -= 1;
    }
  }
  return { year, month };
}

/**
 * Strip numbers, dates, card-related noise from a bank posting text and
 * lowercase it. Used both for grouping ("Netto København 1234" and
 * "Netto Aarhus 5678" should group as the same payee) and for the
 * suggest-mapping fuzzy match against fast item names.
 */
export function normalizePayee(raw: string): string {
  return String(raw ?? '')
    .toLowerCase()
    .replace(/\b\d[\d.,/-]*\b/g, ' ')
    .replace(/\b(købmand|kort|visa|dankort|betaling|nets|automatisk|overførsel|mobilepay)\b/g, ' ')
    .replace(/[^a-zæøå ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseDanishNumber(raw: string): number | null {
  const trimmed = String(raw ?? '').trim();
  if (trimmed === '') return null;
  const normalized = trimmed.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  if (Number.isNaN(n)) return null;
  return Math.round(n);
}

function parseDate(raw: string): Date | null {
  const m = String(raw ?? '').match(/(\d{1,4})[.\/-](\d{1,2})[.\/-](\d{1,4})/);
  if (!m) return null;
  const a = +m[1]!;
  const mo = +m[2]!;
  const b = +m[3]!;
  // First segment > 31 → yyyy-mm-dd. Otherwise dd-mm-yyyy (2-digit year → 2000+).
  const d =
    a > 31
      ? new Date(a, mo - 1, b)
      : new Date(b < 100 ? 2000 + b : b, mo - 1, a);
  return isNaN(d.getTime()) ? null : d;
}

function stripBom(s: string): string {
  return s.startsWith(BOM) ? s.slice(1) : s;
}

function findColumn(header: string[], keywords: string[]): number {
  return header.findIndex((h) => keywords.some((k) => h.includes(k)));
}

/**
 * @throws Error with a Danish message when parsing fails.
 */
export function parseBankStatement(
  text: string,
  opts: BankParseOptions = {},
): BankParseResult {
  const budgetStartDay = Math.max(1, Math.min(28, opts.budgetStartDay ?? 1));
  const lines = stripBom(text)
    .split(/\r?\n/)
    .filter((l) => l.trim() !== '');
  if (lines.length < 2) {
    throw new Error('Filen er tom eller mangler rækker.');
  }
  // Auto-detect delimiter: whichever appears more often on the header row wins.
  const first = lines[0]!;
  const semiCount = (first.match(/;/g) ?? []).length;
  const commaCount = (first.match(/,/g) ?? []).length;
  const delim = semiCount >= commaCount ? ';' : ',';

  const rows = lines.map((l) =>
    l.split(delim).map((c) => c.trim().replace(/^"|"$/g, '')),
  );
  const header = rows[0]!.map((h) => h.toLowerCase());
  let dateCol = findColumn(header, ['dato', 'date', 'bogf']);
  let textCol = findColumn(header, ['tekst', 'beskriv', 'text', 'posteringstekst', 'detalj']);
  let amtCol = findColumn(header, ['beløb', 'beloeb', 'amount', 'hævet', 'trukket']);
  // Fallback: assume [dato, tekst, beløb] order.
  let skippedYear = 0;
  if (dateCol === -1) dateCol = 0;
  if (textCol === -1) textCol = 1;
  if (amtCol === -1) amtCol = rows[0]!.length >= 3 ? 2 : rows[0]!.length - 1;

  const txns: BankTransaction[] = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i]!;
    if (r.length < 2) continue;
    const d = parseDate(r[dateCol] ?? '');
    if (!d) continue;
    const amt = parseDanishNumber(r[amtCol] ?? '');
    if (amt === null) continue;
    if (amt >= 0) continue; // only expenses (negative amounts)
    const { year, month } = budgetPeriodFor(d, budgetStartDay);
    if (opts.targetYear !== undefined && year !== opts.targetYear) {
      skippedYear++;
      continue;
    }
    txns.push({
      month,
      text: r[textCol] ?? '(ukendt)',
      amount: Math.abs(amt),
    });
  }
  if (txns.length === 0 && skippedYear === 0) {
    throw new Error(
      'Ingen udgiftsposteringer fundet. Tjek at filen har dato-, tekst- og beløbskolonner.',
    );
  }

  const map = new Map<string, BankPayeeGroup>();
  for (const t of txns) {
    const key = normalizePayee(t.text) || t.text.toLowerCase();
    let g = map.get(key);
    if (!g) {
      g = { id: key, label: t.text, count: 0, total: 0, months: {} };
      map.set(key, g);
    }
    g.count++;
    g.total += t.amount;
    g.months[t.month] = (g.months[t.month] ?? 0) + t.amount;
  }
  const groups = [...map.values()].sort((a, b) => b.total - a.total);
  return { groups, txnCount: txns.length, skippedYearCount: skippedYear };
}

interface FastItemRef {
  key: string; // "categoryId:itemId" used as select-value in the UI
  categoryName: string;
  itemName: string;
}

/**
 * Suggest an initial mapping from payee groups → fast items using word
 * overlap on normalized text. Returns { [groupId]: itemKey | '' }.
 */
export function suggestBankMapping(
  groups: BankPayeeGroup[],
  items: FastItemRef[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const g of groups) {
    const gl = normalizePayee(g.label);
    const words = gl.split(' ').filter((w) => w.length >= 3);
    let best: string | null = null;
    let bestScore = 0;
    for (const it of items) {
      const il = it.itemName.toLowerCase();
      let score = 0;
      for (const w of words) if (il.includes(w)) score++;
      const firstWord = il.split(' ')[0];
      if (firstWord && gl.includes(firstWord)) score++;
      if (score > bestScore) {
        bestScore = score;
        best = it.key;
      }
    }
    out[g.id] = bestScore > 0 && best ? best : '';
  }
  return out;
}
